import cx from 'classnames';
import { Button, Form } from 'components/admin/ui';
import _ from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import css from './CheckboxGroup.module.scss';

export interface ICheckboxOption {
  label: ReactNode;
  value: string;
}

interface ICheckboxGroup {
  disabled?: boolean;
  minimumOptionsCount?: number;
  name: string;
  onChange?: (value: string[]) => void;
  options: ICheckboxOption[];
  title: string;
  value: string[];
}

const DEFAULT_VISIBLE_OPTIONS_LENGTH = 3;
const MINIMUM_OPTIONS_COUNT = 2;

export const CheckboxGroup = ({
  disabled = false,
  minimumOptionsCount = MINIMUM_OPTIONS_COUNT,
  name,
  onChange,
  options,
  title,
  value,
}: ICheckboxGroup) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleOptionsLength =
    options.length === DEFAULT_VISIBLE_OPTIONS_LENGTH + 1
      ? DEFAULT_VISIBLE_OPTIONS_LENGTH + 1
      : DEFAULT_VISIBLE_OPTIONS_LENGTH;

  useMemo(() => {
    if (!isExpanded) {
      const visibleValues = _.map(
        _.slice(options, 0, visibleOptionsLength),
        (option) => option.value
      );
      const hiddenSelectedValues = _.difference(value, visibleValues);

      if (hiddenSelectedValues.length) {
        setIsExpanded(true);
      }
    }
  }, [options]);

  if (_.size(options) < minimumOptionsCount) {
    return null;
  }

  return (
    <fieldset className={css.container}>
      <legend className={css.title}>{title}</legend>
      {_.map(
        _.slice(options, 0, isExpanded ? options.length : visibleOptionsLength),
        (o) => {
          const handleChange = () => {
            const valueSet = new Set(value);
            valueSet.has(o.value)
              ? valueSet.delete(o.value)
              : valueSet.add(o.value);
            onChange(Array.from(valueSet));
          };
          return (
            <Form.Checkbox
              checked={_.includes(value, o.value)}
              className={cx(css.checkbox, {
                [css.highlight]: _.includes(value, o.value),
              })}
              key={o.value}
              label={o.label}
              name={name}
              onChange={handleChange}
              disabled={disabled}
            />
          );
        }
      )}
      {options.length > visibleOptionsLength && (
        <Button
          className={css.expandButton}
          variant="link"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded
            ? 'Show fewer options'
            : `See all ${options.length} options`}
        </Button>
      )}
    </fieldset>
  );
};
